import { useState, useEffect } from "react";
import { useDrive } from "../App";
import { Sidebar } from "./Dashboard";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { FileText, Download, FileSpreadsheet, FileCheck, Loader2, Share2 } from "lucide-react";

export default function Reports() {
  const { initialized, folderIds, listFiles, getFileAsJson, uploadFile, createShareLink } = useDrive();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [exportOptions, setExportOptions] = useState({
    monthlyPdf: true,
    vatCsv: true,
    transactionsCsv: true
  });
  const [exporting, setExporting] = useState(false);

  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  const loadMonthData = async () => {
    const receipts = [];
    const transactions = [];
    
    // Load receipts
    const monthFolders = await listFiles(folderIds.receipts, 'application/vnd.google-apps.folder');
    const targetFolder = monthFolders.find(f => f.name === selectedMonth);
    
    if (targetFolder) {
      const files = await listFiles(targetFolder.id);
      for (const f of files.filter(f => f.name.endsWith('.json'))) {
        try {
          receipts.push(await getFileAsJson(f.id));
        } catch (e) {}
      }
    }
    
    // Load transactions
    const statementFiles = await listFiles(folderIds.bank_statements);
    const statementFile = statementFiles.find(f => f.name === `${selectedMonth}.json`);
    if (statementFile) {
      try {
        const statement = await getFileAsJson(statementFile.id);
        transactions.push(...(statement.transactions || []));
      } catch (e) {}
    }
    
    return { receipts, transactions };
  };

  const generatePDF = async () => {
    setExporting(true);
    try {
      const { receipts, transactions } = await loadMonthData();
      
      const doc = new jsPDF();
      const monthLabel = new Date(selectedMonth + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      
      // Title Page
      doc.setFontSize(24);
      doc.setTextColor(15, 23, 42);
      doc.text("Monthly Financial Report", 20, 30);
      
      doc.setFontSize(16);
      doc.setTextColor(100, 116, 139);
      doc.text(`Period: ${monthLabel}`, 20, 45);
      
      // Summary
      const totalReceipts = receipts.length;
      const matchedReceipts = receipts.filter(r => r.isMatched).length;
      const totalVat = receipts.reduce((sum, r) => sum + (r.vatAmount || 0), 0);
      const totalSpend = receipts.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
      
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      
      autoTable(doc, {
        startY: 60,
        head: [['Metric', 'Value']],
        body: [
          ['Total Receipts', String(totalReceipts)],
          ['Matched Receipts', String(matchedReceipts)],
          ['Total Transactions', String(transactions.length)],
          ['Total VAT Paid', `£${totalVat.toFixed(2)}`],
          ['Total Spend', `£${totalSpend.toFixed(2)}`]
        ],
        theme: 'grid',
        headStyles: { fillColor: [15, 23, 42] }
      });
      
      // Transactions Page
      if (transactions.length > 0) {
        doc.addPage();
        doc.setFontSize(18);
        doc.text("Bank Transactions", 20, 20);
        
        autoTable(doc, {
          startY: 30,
          head: [['Date', 'Description', 'Amount', 'Status']],
          body: transactions.map(t => [
            t.date,
            t.description?.slice(0, 40),
            `£${t.amount?.toFixed(2)}`,
            t.isMatched ? 'Matched' : 'Unmatched'
          ]),
          theme: 'grid',
          headStyles: { fillColor: [15, 23, 42] }
        });
      }
      
      // Receipts Page
      if (receipts.length > 0) {
        doc.addPage();
        doc.setFontSize(18);
        doc.text("Receipts", 20, 20);
        
        autoTable(doc, {
          startY: 30,
          head: [['Date', 'Merchant', 'Total', 'VAT', 'Status']],
          body: receipts.map(r => [
            r.transactionDate?.slice(0, 10),
            r.merchantName?.slice(0, 30),
            `£${(r.totalAmount || 0).toFixed(2)}`,
            `£${(r.vatAmount || 0).toFixed(2)}`,
            r.isMatched ? 'Matched' : 'Unmatched'
          ]),
          theme: 'grid',
          headStyles: { fillColor: [15, 23, 42] }
        });
      }
      
      // Save to Drive
      const pdfBlob = doc.output('blob');
      await uploadFile(`${selectedMonth}_Combined.pdf`, pdfBlob, 'application/pdf', folderIds.exports);
      
      // Also download
      doc.save(`report_${selectedMonth}.pdf`);
      
      toast.success("Monthly PDF report generated and saved to Drive");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setExporting(false);
    }
  };

  const generateVATCSV = async () => {
    setExporting(true);
    try {
      const { receipts } = await loadMonthData();
      
      const rows = [
        ["Date", "Merchant", "Category", "Net Amount", "VAT Rate", "VAT Amount", "Total", "Matched"],
        ...receipts.map(r => [
          r.transactionDate?.slice(0, 10),
          r.merchantName,
          r.category || '',
          (r.totalAmount - (r.vatAmount || 0)).toFixed(2),
          r.vatRate || '',
          (r.vatAmount || 0).toFixed(2),
          (r.totalAmount || 0).toFixed(2),
          r.isMatched ? 'Yes' : 'No'
        ])
      ];
      
      const csv = rows.map(r => r.join(",")).join("\n");
      
      // Save to Drive
      await uploadFile(`${selectedMonth}_VAT_Summary.csv`, csv, 'text/csv', folderIds.vat_reports);
      
      // Download
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vat_report_${selectedMonth}.csv`;
      a.click();
      
      toast.success("VAT CSV exported and saved to Drive");
    } catch (error) {
      console.error("CSV generation error:", error);
      toast.error("Failed to generate VAT CSV");
    } finally {
      setExporting(false);
    }
  };

  const generateTransactionsCSV = async () => {
    setExporting(true);
    try {
      const { transactions } = await loadMonthData();
      
      const rows = [
        ["Date", "Description", "Reference", "Amount", "Type", "Matched", "Receipt ID"],
        ...transactions.map(t => [
          t.date,
          t.description,
          t.reference || '',
          t.amount?.toFixed(2),
          t.amount < 0 ? 'Expense' : 'Income',
          t.isMatched ? 'Yes' : 'No',
          t.matchedReceiptId || ''
        ])
      ];
      
      const csv = rows.map(r => r.join(",")).join("\n");
      
      // Download
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions_${selectedMonth}.csv`;
      a.click();
      
      toast.success("Transactions CSV downloaded");
    } catch (error) {
      console.error("CSV generation error:", error);
      toast.error("Failed to generate transactions CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleAccountantExport = async () => {
    if (!exportOptions.monthlyPdf && !exportOptions.vatCsv && !exportOptions.transactionsCsv) {
      toast.error("Select at least one export option");
      return;
    }
    
    if (exportOptions.monthlyPdf) await generatePDF();
    if (exportOptions.vatCsv) await generateVATCSV();
    if (exportOptions.transactionsCsv) await generateTransactionsCSV();
    
    toast.success("All selected reports exported!");
  };

  if (!initialized) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="reports" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeItem="reports" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12 max-w-5xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reports & Export
            </h1>
            <p className="text-slate-600 mt-1">Generate accountant-ready reports (saved to your Google Drive)</p>
          </div>

          {/* Report Settings */}
          <Card className="mb-8 bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-slate-900">Report Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Report Period</Label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-[250px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Export Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Monthly PDF Report</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Combined report with summary, transactions, and receipts.
                </p>
                <Button className="w-full" onClick={generatePDF} disabled={exporting}>
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Generate PDF
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">VAT Summary CSV</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Receipt-level VAT breakdown with amounts and rates.
                </p>
                <Button className="w-full" variant="outline" onClick={generateVATCSV} disabled={exporting}>
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download CSV
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Transactions CSV</h3>
                <p className="text-sm text-slate-600 mb-4">
                  All transactions with linked receipt IDs for audit.
                </p>
                <Button className="w-full" variant="outline" onClick={generateTransactionsCSV} disabled={exporting}>
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download CSV
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Accountant Export */}
          <Card className="bg-slate-900 text-white shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">Accountant Export Mode</h3>
                  <p className="text-slate-300 mb-6">
                    One-click export of all reports. Files are saved to your Google Drive for easy sharing.
                  </p>

                  <div className="flex flex-wrap gap-6 mb-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="opt-pdf"
                        checked={exportOptions.monthlyPdf}
                        onCheckedChange={(checked) => setExportOptions({ ...exportOptions, monthlyPdf: !!checked })}
                        className="border-slate-500 data-[state=checked]:bg-white data-[state=checked]:text-slate-900"
                      />
                      <Label htmlFor="opt-pdf" className="text-sm text-slate-300 cursor-pointer">Monthly Combined PDF</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="opt-vat"
                        checked={exportOptions.vatCsv}
                        onCheckedChange={(checked) => setExportOptions({ ...exportOptions, vatCsv: !!checked })}
                        className="border-slate-500 data-[state=checked]:bg-white data-[state=checked]:text-slate-900"
                      />
                      <Label htmlFor="opt-vat" className="text-sm text-slate-300 cursor-pointer">VAT Breakdown CSV</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="opt-txn"
                        checked={exportOptions.transactionsCsv}
                        onCheckedChange={(checked) => setExportOptions({ ...exportOptions, transactionsCsv: !!checked })}
                        className="border-slate-500 data-[state=checked]:bg-white data-[state=checked]:text-slate-900"
                      />
                      <Label htmlFor="opt-txn" className="text-sm text-slate-300 cursor-pointer">Transactions CSV</Label>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    className="bg-white text-slate-900 hover:bg-slate-100"
                    onClick={handleAccountantExport}
                    disabled={exporting}
                  >
                    {exporting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
                    Export All Reports
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
